import type { EventBatchRes, OutboxStreamKind } from '#shared/core/event-batch';
import { MAX_EVENT_BATCH } from '#shared/core/event-batch';
import { isApiError, isRetryable, postJson } from './api';
import { hasIndexedDB, type OutboxRow, resetDb, withDb } from './idb';

/**
 * Outbox (A9.5). Generic over streams: game events, drill attempts and Lab events all wait here
 * until there is signal, then go up in batches of at most 200.
 *
 *   registerOutboxStream('game', { url: (id) => `/api/games/${id}/events` });
 *   await enqueueCreate({ kind: 'game', id: gameId }, { url: api.url(createGame), body: gameCreate });
 *   await enqueue({ kind: 'game', id: gameId }, event);   // event.id is the uuidv7 dedupe key
 *
 * Creates (frozen rule, docs/CONTRACTS.md "Offline creates"):
 * - A game and a drill session are made by their own idempotent POST. `enqueueCreate` queues that
 *   POST as the stream's first row. The stream is HELD until it lands: its events never go up
 *   before it, so a game started with no signal reaches the server whole. Always call it before
 *   the first `enqueue` of the stream, even when you are online (it posts at once and is gone).
 * - A Lab run needs no create, its start event creates the run.
 * - `replaceCreate` swaps the body of a create that is still queued (a game that was meant for a
 *   sitter but has to stay on this phone). `enqueueCreate` itself never overwrites: the first one
 *   wins, like every other row.
 * - A create can name another stream's create it depends on (`after`): it is held until that one
 *   has landed or been parked. An Iso Gauntlet game names its drill session this way, because the
 *   server drops a drill id it has never seen.
 * - A create that fails for good (4xx) is parked as rejected with the server code, and the stream's
 *   events are parked with the code `create_failed`. `requeue(ids)` puts rejected rows back.
 *
 * Flushing: after every enqueue, on `online`, when the app becomes visible, and at start, with
 * backoff 1 s, 2 s, 4 s up to 60 s. Accepted and duplicate ids leave the outbox. Rejected ids
 * stay, marked with their code, so the module can say "These plays didn't count". Statuses:
 * - 401, 428, 426: stop and keep every row pending (signed out, install gate, app too old; an
 *   old phone with offline plays must not lose them to a forced update).
 * - network, 5xx, 408, 429: retry later.
 * - 404 on a batch while the stream still has a create queued: retry.
 * - 400 VALIDATION on a batch of more than one: the batch is split in halves until the bad event
 *   stands alone, and only that one is parked as 'invalid'. The route validates the whole body
 *   before its handler, so one malformed event must not sink 199 good ones.
 * - any other 4xx: the batch is parked with the lowercased error code.
 *
 * The outbox is a transit queue, not the game log: accepted rows leave it. A module that must
 * rebuild its fold after a reload keeps its own log (kvSet in idb.ts).
 *
 * Status for the UI (`status()`, `useOutboxStatus()`, the SyncChip in the shell): how many plays
 * and creates wait, how many were parked, whether a flush runs, whether the flush is blocked by
 * the sign-in, the install gate or a forced update, and whether this phone could not open its
 * database (then rows live in memory only and the chip says so; the store probes the database on
 * every flush and moves the rows back into it as soon as it answers).
 *
 * Safety nets: every POST has a timeout (a hung request on a bad court connection must not hold
 * the queue), a database that fails to open falls back to memory instead of throwing into the
 * scorer, and `startOutbox()` retries on a 15 s heartbeat as well as on online, visible and start.
 */

export interface StreamRef {
  kind: OutboxStreamKind;
  id: string;
}

/** The request a stream needs before its first event, for example POST /api/games. */
export interface CreateRequest {
  url: string;
  body: unknown;
  /**
   * Another stream whose queued create must land first (a game that counts for a drill session).
   * The create waits while that stream still has a create pending here.
   */
  after?: StreamRef;
}

export interface OutboxStreamConfig {
  /** The batch endpoint for one stream, e.g. (id) => `/api/games/${id}/events`. */
  url: (streamId: string) => string;
  /** Items per POST. Defaults to (and is capped at) MAX_EVENT_BATCH. */
  max?: number;
  /** Request body from the items. Default `{ events: items }` (drills use `{ attempts }`). */
  body?: (items: unknown[]) => unknown;
  /** Sees every batch response, for example to update the local fold with serverSeq. */
  onResult?: (streamId: string, res: EventBatchRes) => void;
  /** Sees the answer of a stream's queued create (the stored game or drill session). */
  onCreated?: (streamId: string, res: unknown) => void;
  /**
   * Awaited before every batch of the stream goes up (never before its create). Combine uses it to
   * let a drill undo reach the server before the shot that replaces it. A throw or a hang past
   * `BEFORE_BATCH_TIMEOUT_MS` is ignored, so it can delay a batch but never hold the queue.
   */
  beforeBatch?: (streamId: string) => Promise<void>;
}

export interface OutboxStore {
  /** True when the store gave up its database and now keeps rows in memory only. */
  degraded?(): boolean;
  put(row: OutboxRow): Promise<void>;
  /** Overwrite a row that is still pending. False when there is no such pending row (landed, parked or never queued). */
  replace(row: OutboxRow): Promise<boolean>;
  /** Pending rows of one stream, oldest first. */
  pending(kind: OutboxStreamKind, streamId: string): Promise<OutboxRow[]>;
  /** Every stream that has pending rows. */
  streams(): Promise<StreamRef[]>;
  remove(ids: string[]): Promise<void>;
  reject(items: { id: string; code: string }[]): Promise<void>;
  bumpTries(ids: string[]): Promise<void>;
  /** Put rejected rows back to pending with a clean slate. */
  requeue(ids: string[]): Promise<void>;
  all(): Promise<OutboxRow[]>;
}

/** POST JSON and return the parsed answer (an EventBatchRes for batches, the stored row for creates). */
export type Poster = (url: string, body: unknown) => Promise<unknown>;

/** Why the flush stopped without losing anything: the sign-in, the install gate or an old build. */
export type OutboxBlock = 'signed_out' | 'install' | 'update';

export interface OutboxStatus {
  /** Plays waiting for signal (creates not counted). */
  waiting: number;
  /** Game or drill creates waiting for signal. */
  creates: number;
  /** Rows the server refused (they sit there until dismissed or requeued). */
  rejected: number;
  flushing: boolean;
  blocked: OutboxBlock | null;
  /** The phone says it has no connection. */
  offline: boolean;
  /** The database could not be opened, so rows would not survive an app restart. */
  degraded: boolean;
}

export interface Outbox {
  register(kind: OutboxStreamKind, cfg: OutboxStreamConfig): void;
  enqueue(stream: StreamRef, event: { id: string }): Promise<void>;
  /** Queue the POST that creates the stream's game or drill session. Idempotent per stream. Call it before the first `enqueue`. */
  enqueueCreate(stream: StreamRef, request: CreateRequest): Promise<void>;
  /**
   * Swap the request of the stream's create while it is still queued. Resolves 'replaced', or
   * 'gone' when the create is no longer pending (it already landed, or it was parked): nothing is
   * queued then, because a second create would only be answered with the stored game.
   * It waits for a flush in flight first and holds the create meanwhile, so the old body can never
   * be posted after the swap.
   */
  replaceCreate(stream: StreamRef, request: CreateRequest): Promise<'replaced' | 'gone'>;
  /** Try now, whatever blocked the last attempt. */
  flush(): Promise<void>;
  /** The heartbeat: like flush, but leaves a blocked queue alone until something changes. */
  tick(): Promise<void>;
  status(): Promise<OutboxStatus>;
  /** Pending events (not rejected, creates not counted), optionally for one kind or one stream. */
  count(filter?: { kind?: OutboxStreamKind; id?: string }): Promise<number>;
  /** Rejected rows, creates included (`op === 'create'`). */
  rejected(filter?: { kind?: OutboxStreamKind; id?: string }): Promise<OutboxRow[]>;
  /** Drop rejected rows once the user has seen them. */
  dismiss(ids: string[]): Promise<void>;
  /** Try rejected rows again (a create that failed, a code that was fixed server-side). */
  requeue(ids: string[]): Promise<void>;
  subscribe(cb: () => void): () => void;
}

const BACKOFF_MS = [1000, 2000, 4000, 8000, 16000, 32000, 60000];

/** Longest a stream's `beforeBatch` hook may delay a batch. */
export const BEFORE_BATCH_TIMEOUT_MS = 8000;

const createRowId = (s: StreamRef) => `create:${s.kind}:${s.id}`;

export function createOutbox(deps: {
  store: OutboxStore;
  post: Poster;
  now?: () => number;
  online?: () => boolean;
  schedule?: (fn: () => void, ms: number) => unknown;
}): Outbox {
  const streams = new Map<OutboxStreamKind, OutboxStreamConfig>();
  const listeners = new Set<() => void>();
  const now = deps.now ?? Date.now;
  const online = deps.online ?? (() => true);
  const schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  let order = 0;
  let flushing: Promise<void> | null = null;
  let again = false;
  let failures = 0;
  let timer = false;
  let blocked: OutboxBlock | null = null;
  /** Create rows being replaced right now: the flush leaves them alone. */
  const replacing = new Set<string>();

  const emit = () => {
    for (const l of listeners) l();
  };

  function retryLater() {
    if (timer) return;
    timer = true;
    const ms = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)] ?? 60000;
    schedule(() => {
      timer = false;
      void flush();
    }, ms);
  }

  const STOP_STATUS = new Set([401, 426, 428]);
  const blockFor = (status: number): OutboxBlock =>
    status === 401 ? 'signed_out' : status === 426 ? 'update' : 'install';
  const isCreate = (r: OutboxRow) => r.op === 'create';
  const errCode = (e: unknown) => (isApiError(e) ? e.code.toLowerCase() : 'invalid');

  /** The stream's queued create: post it, then let the loop carry on. */
  async function sendCreate(
    cfg: OutboxStreamConfig,
    streamId: string,
    all: OutboxRow[],
  ): Promise<'next' | 'retry' | 'stop' | 'held'> {
    const row = all.find(isCreate) as OutboxRow;
    const req = row.request;
    if (!req) {
      await deps.store.reject([{ id: row.id, code: 'invalid' }]);
      emit();
      return 'next';
    }
    // The create this one names must land first. A parked dependency no longer holds anything.
    if (req.after && (await deps.store.pending(req.after.kind, req.after.id)).some(isCreate)) return 'held';
    try {
      const res = await deps.post(req.url, req.body);
      await deps.store.remove([row.id]);
      cfg.onCreated?.(streamId, res);
      emit();
      return 'next';
    } catch (e) {
      await deps.store.bumpTries([row.id]);
      if (isApiError(e) && STOP_STATUS.has(e.status)) {
        blocked = blockFor(e.status);
        return 'stop';
      }
      if (isRetryable(e)) return 'retry';
      // The game or session cannot be made, so its events can never count either.
      const code = errCode(e);
      await deps.store.reject([
        { id: row.id, code },
        ...all.filter((r) => !isCreate(r)).map((r) => ({ id: r.id, code: 'create_failed' })),
      ]);
      emit();
      return 'next';
    }
  }

  /** One POST of up to `max` events. Splits a 400 batch to find the bad event. */
  async function postRows(
    kind: OutboxStreamKind,
    streamId: string,
    cfg: OutboxStreamConfig,
    rows: OutboxRow[],
  ): Promise<'ok' | 'retry' | 'stop'> {
    const items = rows.map((r) => r.payload);
    const body = cfg.body ? cfg.body(items) : { events: items };
    let res: EventBatchRes;
    try {
      res = (await deps.post(cfg.url(streamId), body)) as EventBatchRes;
    } catch (e) {
      await deps.store.bumpTries(rows.map((r) => r.id));
      if (isApiError(e) && STOP_STATUS.has(e.status)) {
        blocked = blockFor(e.status);
        return 'stop';
      }
      if (isRetryable(e)) return 'retry';
      if (isApiError(e) && e.status === 404 && (await deps.store.pending(kind, streamId)).some(isCreate)) {
        return 'retry';
      }
      const validation = isApiError(e) && e.status === 400 && e.code === 'VALIDATION';
      if (validation && rows.length > 1) {
        const mid = rows.length >> 1;
        const first = await postRows(kind, streamId, cfg, rows.slice(0, mid));
        if (first !== 'ok') return first;
        return postRows(kind, streamId, cfg, rows.slice(mid));
      }
      // A 4xx for the whole batch (locked, gone, one bad event on its own): park the rows for review.
      const code = validation ? 'invalid' : errCode(e);
      await deps.store.reject(rows.map((r) => ({ id: r.id, code })));
      emit();
      return 'ok';
    }
    const done = [...res.accepted.map((a) => a.id), ...res.duplicates];
    await deps.store.remove(done);
    if (res.rejected.length) await deps.store.reject(res.rejected);
    // Anything the server did not mention stays pending; stop looping on it this round.
    const mentioned = new Set([...done, ...res.rejected.map((r) => r.id)]);
    cfg.onResult?.(streamId, res);
    emit();
    return rows.some((r) => !mentioned.has(r.id)) ? 'retry' : 'ok';
  }

  /** Run the stream's `beforeBatch` hook, bounded: it may delay a batch, never hold the queue. */
  async function beforeBatch(cfg: OutboxStreamConfig, streamId: string): Promise<void> {
    if (!cfg.beforeBatch) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        cfg.beforeBatch(streamId),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, BEFORE_BATCH_TIMEOUT_MS);
        }),
      ]);
    } catch {
      // the hook is a courtesy; the batch goes up anyway
    } finally {
      clearTimeout(timer);
    }
  }

  async function flushStream(kind: OutboxStreamKind, streamId: string): Promise<'ok' | 'retry' | 'stop' | 'held'> {
    const cfg = streams.get(kind);
    if (!cfg) return 'ok'; // the module has not registered yet; keep the rows
    const max = Math.min(cfg.max ?? MAX_EVENT_BATCH, MAX_EVENT_BATCH);
    for (;;) {
      const all = await deps.store.pending(kind, streamId);
      const create = all.find(isCreate);
      if (create) {
        // A create being replaced is left alone; the replace flushes again when it is done.
        if (replacing.has(create.id)) return 'ok';
        // The stream is held until its create has landed.
        const r = await sendCreate(cfg, streamId, all);
        if (r === 'next') continue;
        return r;
      }
      const rows = all.slice(0, max);
      if (rows.length === 0) return 'ok';
      await beforeBatch(cfg, streamId);
      const r = await postRows(kind, streamId, cfg, rows);
      if (r !== 'ok') return r;
    }
  }

  async function runFlush(): Promise<void> {
    if (!online()) return;
    blocked = null;
    const list = await deps.store.streams();
    let retry = false;
    const held: StreamRef[] = [];
    for (const s of list) {
      const r = await flushStream(s.kind, s.id);
      if (r === 'retry') retry = true;
      if (r === 'held') held.push(s);
      if (r === 'stop') return;
    }
    // A stream that waited for another stream's create gets one more go now that the others ran.
    for (const s of held) {
      const r = await flushStream(s.kind, s.id);
      if (r === 'retry' || r === 'held') retry = true;
      if (r === 'stop') return;
    }
    if (retry) {
      failures++;
      retryLater();
    } else {
      failures = 0;
    }
  }

  function flush(): Promise<void> {
    if (flushing) {
      again = true;
      return flushing;
    }
    flushing = (async () => {
      try {
        do {
          again = false;
          await runFlush();
        } while (again);
      } finally {
        flushing = null;
        emit();
      }
    })();
    emit();
    return flushing;
  }

  function tick(): Promise<void> {
    return blocked ? Promise.resolve() : flush();
  }

  return {
    register(kind, cfg) {
      streams.set(kind, cfg);
      void flush();
    },
    async enqueue(stream, event) {
      const t = now();
      order = Math.max(order + 1, t * 1000);
      await deps.store.put({
        id: event.id,
        kind: stream.kind,
        streamId: stream.id,
        payload: event,
        order,
        enqueuedAt: t,
        state: 'pending',
        tries: 0,
      });
      emit();
      void flush();
    },
    async enqueueCreate(stream, request) {
      const t = now();
      order = Math.max(order + 1, t * 1000);
      await deps.store.put({
        id: createRowId(stream),
        kind: stream.kind,
        streamId: stream.id,
        payload: request.body,
        order,
        enqueuedAt: t,
        state: 'pending',
        tries: 0,
        op: 'create',
        request,
      });
      emit();
      void flush();
    },
    async replaceCreate(stream, request) {
      const id = createRowId(stream);
      replacing.add(id);
      try {
        // A POST in flight may still carry the old body. Let it finish: if it lands, the row is gone.
        if (flushing) await flushing.catch(() => {});
        const row = (await deps.store.pending(stream.kind, stream.id)).find((r) => r.id === id);
        if (!row) return 'gone';
        const swapped = await deps.store.replace({
          ...row,
          payload: request.body,
          request,
          tries: 0,
        });
        return swapped ? 'replaced' : 'gone';
      } finally {
        replacing.delete(id);
        emit();
        void flush();
      }
    },
    flush,
    tick,
    async status() {
      const rows = await deps.store.all();
      return {
        waiting: rows.filter((r) => r.state === 'pending' && r.op !== 'create').length,
        creates: rows.filter((r) => r.state === 'pending' && r.op === 'create').length,
        rejected: rows.filter((r) => r.state === 'rejected').length,
        flushing: flushing !== null,
        blocked,
        offline: !online(),
        degraded: deps.store.degraded?.() ?? false,
      };
    },
    async count(filter = {}) {
      const rows = await deps.store.all();
      return rows.filter(
        (r) =>
          r.state === 'pending' &&
          r.op !== 'create' &&
          (!filter.kind || r.kind === filter.kind) &&
          (!filter.id || r.streamId === filter.id),
      ).length;
    },
    async rejected(filter = {}) {
      const rows = await deps.store.all();
      return rows.filter(
        (r) =>
          r.state === 'rejected' &&
          (!filter.kind || r.kind === filter.kind) &&
          (!filter.id || r.streamId === filter.id),
      );
    },
    async dismiss(ids) {
      await deps.store.remove(ids);
      emit();
    },
    async requeue(ids) {
      await deps.store.requeue(ids);
      emit();
      void flush();
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

/** In-memory store for tests and for browsers without IndexedDB. */
export function memoryOutboxStore(): OutboxStore {
  const rows = new Map<string, OutboxRow>();
  const sorted = () => [...rows.values()].sort((a, b) => a.order - b.order);
  return {
    async put(row) {
      if (!rows.has(row.id)) rows.set(row.id, row);
    },
    async replace(row) {
      if (rows.get(row.id)?.state !== 'pending') return false;
      rows.set(row.id, row);
      return true;
    },
    async pending(kind, streamId) {
      return sorted().filter((r) => r.kind === kind && r.streamId === streamId && r.state === 'pending');
    },
    async streams() {
      const seen = new Map<string, StreamRef>();
      for (const r of sorted())
        if (r.state === 'pending') seen.set(`${r.kind}:${r.streamId}`, { kind: r.kind, id: r.streamId });
      return [...seen.values()];
    },
    async remove(ids) {
      for (const id of ids) rows.delete(id);
    },
    async reject(items) {
      for (const it of items) {
        const r = rows.get(it.id);
        if (r) rows.set(it.id, { ...r, state: 'rejected', code: it.code });
      }
    },
    async bumpTries(ids) {
      for (const id of ids) {
        const r = rows.get(id);
        if (r) rows.set(id, { ...r, tries: r.tries + 1 });
      }
    },
    async requeue(ids) {
      for (const id of ids) {
        const r = rows.get(id);
        if (r && r.state === 'rejected') rows.set(id, { ...r, state: 'pending', code: undefined, tries: 0 });
      }
    },
    async all() {
      return sorted();
    },
  };
}

/**
 * The IndexedDB-backed store (DB `tts`, store `outbox`, index `stream` on [kind, streamId]). Every
 * operation goes through `withDb`, so a connection that WebKit dropped while the app slept is
 * reopened once instead of failing the play.
 */
export function idbOutboxStore(): OutboxStore {
  return {
    put: (row) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        // First write wins: re-enqueueing the same event id is a no-op, like the server.
        if (!(await tx.store.get(row.id))) await tx.store.put(row);
        await tx.done;
      }),
    replace: (row) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        const had = await tx.store.get(row.id);
        const ok = had?.state === 'pending';
        if (ok) await tx.store.put(row);
        await tx.done;
        return ok;
      }),
    pending: (kind, streamId) =>
      withDb(async (d) => {
        const rows = await d.getAllFromIndex('outbox', 'stream', [kind, streamId]);
        return rows.filter((r) => r.state === 'pending').sort((a, b) => a.order - b.order);
      }),
    streams: () =>
      withDb(async (d) => {
        const rows = await d.getAllFromIndex('outbox', 'state', 'pending');
        const seen = new Map<string, StreamRef>();
        for (const r of rows.sort((a, b) => a.order - b.order))
          seen.set(`${r.kind}:${r.streamId}`, { kind: r.kind, id: r.streamId });
        return [...seen.values()];
      }),
    remove: (ids) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        await Promise.all(ids.map((id) => tx.store.delete(id)));
        await tx.done;
      }),
    reject: (items) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        for (const it of items) {
          const r = await tx.store.get(it.id);
          if (r) await tx.store.put({ ...r, state: 'rejected', code: it.code });
        }
        await tx.done;
      }),
    bumpTries: (ids) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        for (const id of ids) {
          const r = await tx.store.get(id);
          if (r) await tx.store.put({ ...r, tries: r.tries + 1 });
        }
        await tx.done;
      }),
    requeue: (ids) =>
      withDb(async (d) => {
        const tx = d.transaction('outbox', 'readwrite');
        for (const id of ids) {
          const r = await tx.store.get(id);
          if (r && r.state === 'rejected') await tx.store.put({ ...r, state: 'pending', code: undefined, tries: 0 });
        }
        await tx.done;
      }),
    all: () => withDb(async (d) => (await d.getAll('outbox')).sort((a, b) => a.order - b.order)),
  };
}

export interface ResilientOptions {
  /** Called before the one retry of a failed primary call (IndexedDB: drop the cached connection). */
  reset?: () => void;
  /** While degraded, probe the primary at most this often. Default 3 s. */
  probeEveryMs?: number;
  now?: () => number;
}

/**
 * A store that tries `primary` (IndexedDB) and, when it keeps throwing (private mode, a full disk,
 * a blocked database), carries on in `fallback` (memory). The scorer keeps scoring and the chip
 * warns that nothing survives a restart.
 *
 * It does not give up on the first throw. A call that fails is retried once on the primary after
 * `reset` (a dropped connection comes back with a fresh open). Only a second failure degrades. While
 * degraded, `streams()` (the first read of every flush) probes the primary: when it answers, the
 * rows kept in memory are copied into it, the memory copy is emptied, and the store switches back.
 * Rows that were safe in the primary all along show up again, so nothing is stranded.
 */
export function resilientOutboxStore(
  primary: OutboxStore,
  fallback: OutboxStore,
  onDegrade?: (e: unknown) => void,
  opts: ResilientOptions = {},
): OutboxStore {
  const now = opts.now ?? Date.now;
  const probeEvery = opts.probeEveryMs ?? 3000;
  let down = false;
  let lastProbe = 0;
  let probing: Promise<void> | null = null;

  const use = async <T>(run: (s: OutboxStore) => Promise<T>): Promise<T> => {
    if (!down) {
      try {
        return await run(primary);
      } catch (first) {
        try {
          opts.reset?.();
          return await run(primary);
        } catch {
          if (!down) {
            down = true;
            lastProbe = now();
            onDegrade?.(first);
          }
        }
      }
    }
    return run(fallback);
  };

  /** Copy memory rows into the primary and switch back once the memory copy is empty. */
  async function recover(): Promise<void> {
    try {
      await primary.all(); // the probe: a read that needs a working connection
      for (let i = 0; i < 3; i++) {
        const rows = await fallback.all();
        if (rows.length === 0) {
          down = false; // no await since the check: nothing slipped into memory in between
          return;
        }
        for (const row of rows) await primary.put(row);
        // Rows that already left the queue in memory (rejected, or retried) keep their newer state.
        const rejected = rows.filter((r) => r.state === 'rejected');
        if (rejected.length) await primary.reject(rejected.map((r) => ({ id: r.id, code: r.code ?? 'invalid' })));
        await fallback.remove(rows.map((r) => r.id));
      }
    } catch {
      // still down: the next probe tries again, and the rows stay in memory
    }
  }

  const probe = (): Promise<void> => {
    if (!down) return Promise.resolve();
    if (probing) return probing;
    if (now() - lastProbe < probeEvery) return Promise.resolve();
    lastProbe = now();
    probing = recover().finally(() => {
      probing = null;
    });
    return probing;
  };

  return {
    degraded: () => down,
    put: (row) => use((s) => s.put(row)),
    replace: (row) => use((s) => s.replace(row)),
    pending: (kind, id) => use((s) => s.pending(kind, id)),
    async streams() {
      await probe();
      return use((s) => s.streams());
    },
    remove: (ids) => use((s) => s.remove(ids)),
    reject: (items) => use((s) => s.reject(items)),
    bumpTries: (ids) => use((s) => s.bumpTries(ids)),
    requeue: (ids) => use((s) => s.requeue(ids)),
    all: () => use((s) => s.all()),
  };
}

/** Longest a single POST may take. A hung request on a bad connection must not hold the queue. */
export const OUTBOX_POST_TIMEOUT_MS = 20_000;

/** How often startOutbox tries again while the app is open, on top of online, visible and start. */
export const OUTBOX_HEARTBEAT_MS = 15_000;

/** The app's outbox. Modules use the named helpers below, never a second instance. */
export const outbox: Outbox = createOutbox({
  store: hasIndexedDB()
    ? resilientOutboxStore(
        idbOutboxStore(),
        memoryOutboxStore(),
        (e) => console.warn('[outbox] database unavailable, keeping plays in memory', e),
        { reset: resetDb },
      )
    : memoryOutboxStore(),
  post: (url, body) => postJson<unknown>(url, body, AbortSignal.timeout(OUTBOX_POST_TIMEOUT_MS)),
  online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
});

export const registerOutboxStream = (kind: OutboxStreamKind, cfg: OutboxStreamConfig): void =>
  outbox.register(kind, cfg);
export const enqueue = (stream: StreamRef, event: { id: string }): Promise<void> => outbox.enqueue(stream, event);
/** Queue the POST that creates a stream's game or drill session. See the header of this file. */
export const enqueueCreate = (stream: StreamRef, request: CreateRequest): Promise<void> =>
  outbox.enqueueCreate(stream, request);
/** Put rejected rows (a failed create, plays that did not count) back in the queue. */
export const requeueOutbox = (ids: string[]): Promise<void> => outbox.requeue(ids);
export const flushOutbox = (): Promise<void> => outbox.flush();

/** Wire the flush triggers once at app start: now, online, visible, and a 15 s heartbeat. */
export function startOutbox(): () => void {
  const onOnline = () => void outbox.flush();
  const onVisible = () => {
    if (document.visibilityState === 'visible') void outbox.flush();
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  const beat = setInterval(() => {
    if (document.visibilityState === 'visible') void outbox.tick();
  }, OUTBOX_HEARTBEAT_MS);
  void outbox.flush();
  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(beat);
  };
}
